/**
 * Cash Casino — the SPIN SESSION seam (solo game, no table clock).
 *
 * PHASE A: practice wallet + uniform stops. This class is now the DEV SANDBOX
 * (`?practice=1`) — Phase D shipped `./serverSession.ts`, which POSTs
 * /casino/spin (one atomic per-user tx, the row born settled) and is what
 * `Page.tsx` imports. The contract held: `spin()` returns the decided result
 * before a reel moves, and `settle()` is only the DISPLAY moment (reels
 * stopped) at which the payout the server already paid becomes visible.
 */
import { type Grid, type Evaluation, gridFor, evaluate, payout, spin as naturalSpin } from './engine';
import { mulberry32, mix } from '../aviator/prng';

export interface Wallet { balance(): number; debit(n: number): boolean; credit(n: number): void; }

export type SpinResult = { id: string; stake: number; stops: [number, number, number]; grid: Grid; ev: Evaluation; paid: number };

export type SpinOutcome = { ok: true; result: SpinResult } | { ok: false; reason: 'balance' | 'busy' };

export interface SpinSession {
  /**
   * Play one spin.
   *
   * The union with `Promise` is what lets ONE Page drive both classes (2026-09-06). The Page
   * aims each reel from `result.stops[i]` the moment this returns, so the decision has to be
   * in hand before anything moves — and on the real machine that decision is a network round
   * trip away (`POST /api/casino/spin` debits and decides in one transaction). So the Page
   * awaits: this class stays exactly as synchronous as it always was, and `ServerSpinSession`
   * gets its ~100 ms. Awaiting also means a server REFUSAL lands before the first reel turns.
   */
  spin(stake: number): SpinOutcome | Promise<SpinOutcome>;
  settle(id: string): number;
  history(): SpinResult[];
  /**
   * Is the balance on screen the REAL one? (2026-09-06)
   *
   * Practice chips are funded locally, so this class knows the figure from the first frame.
   * The server session only learns the player's balance when its first `/state` lands, and
   * until then the Page would otherwise show its seeded opening figure — a number the player
   * does not have — and gate affordability on it. The Page shows `₹—` and holds the machine
   * closed until this goes true.
   */
  walletReady(): boolean;
  /** Server-backed sessions change on their own (a poll landing, an ack); the practice one
   *  never does, so it does not implement this. */
  subscribe?(cb: () => void): () => void;
  /** The server's own plain sentence for the last refusal, when there was one. */
  lastError?(): string | null;
  destroy(): void;
}

export class LocalSpinSession implements SpinSession {
  private rng: () => number;
  private n = 0;
  private open: SpinResult | null = null;
  private hist: SpinResult[] = [];

  constructor(seed: number, private wallet: Wallet) { this.rng = mulberry32(mix(seed, 0xCA51)); }

  spin(stake: number) {
    if (this.open) return { ok: false as const, reason: 'busy' as const };
    if (!this.wallet.debit(stake)) return { ok: false as const, reason: 'balance' as const };
    const stops = naturalSpin(this.rng);                       // PHASE-2 SEAM: the server's scorer picks the triple
    const grid = gridFor(stops), ev = evaluate(grid);
    const result: SpinResult = { id: `s${++this.n}`, stake, stops, grid, ev, paid: payout(stake, ev) };
    this.open = result;
    return { ok: true as const, result };
  }
  /** the reels have stopped: credit what this spin pays (once) */
  settle(id: string) {
    const r = this.open; if (!r || r.id !== id) return 0;
    this.open = null;
    if (r.paid > 0) this.wallet.credit(r.paid);
    this.hist = [r, ...this.hist].slice(0, 20);
    return r.paid;
  }
  history() { return this.hist; }
  /** Practice chips are funded locally, so the figure on screen is never a guess. */
  walletReady() { return true; }
  destroy() { this.open = null; }
}
